import React, { useState } from 'react';
import { IDeviceBreakdown, IDeviceOS, IDeviceDetail } from '../../api';


interface DeviceStatsProps {
  data: IDeviceBreakdown[];
  title: string;
  renderItem: RenderItem;
}

type RenderItem = (value: string) => React.ReactNode;

interface DetailsItemProps {
  details: IDeviceDetail;
  totalCount: number;
  renderItem: RenderItem;
}

const DetailsItem: React.FC<DetailsItemProps> = ({ details, totalCount, renderItem }) => {
  const percentage = ((details.count / totalCount) * 100).toFixed(1);

  return (
    <div className="d-flex justify-content-between align-items-center p-3 ps-5 border-top border-light">
      <div className="d-flex align-items-center gap-3">
        <i className="bi bi-circle-fill text-primary" style={{ fontSize: '0.375rem' }}></i>
        {renderItem(details.name)}
      </div>
      
      <div className="d-flex align-items-center gap-3" style={{ minWidth: '300px' }}>
        <div className="progress flex-grow-1" style={{ height: '0.5rem' }}>
          <div
            className="progress-bar bg-primary"
            style={{ width: `${percentage}%` }}
            role="progressbar"
            aria-valuenow={parseFloat(percentage)}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <small className="text-muted" style={{ minWidth: '48px', textAlign: 'right' }}>
          {percentage}%
        </small>
        <small className="fw-medium text-dark" style={{ minWidth: '64px', textAlign: 'right' }}>
          {details.count.toLocaleString()}
        </small>
      </div>
    </div>
  );
};

interface OSItemProps {
  os: IDeviceOS;
  deviceCount: number;
  isExpanded: boolean;
  onToggle: () => void;
  renderItem: RenderItem;
}

const OSItem: React.FC<OSItemProps> = ({ os, deviceCount, isExpanded, onToggle, renderItem }) => {
  const percentage = ((os.count / deviceCount) * 100).toFixed(1);

  return (
    <div className="border-top">
      <button
        onClick={onToggle}
        className="btn w-100 d-flex justify-content-between align-items-center p-3 ps-5 border-0 text-start bg-light"
      >
        <div className="d-flex align-items-center gap-3">
          <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} text-dark`} style={{ fontSize: '0.875rem' }}></i>
          <span className="fw-medium">{os.name || 'Unknown'}</span>
        </div>
        <div className="d-flex align-items-center gap-3">
          <small className="text-muted">{percentage}%</small>
          <span className="fw-semibold text-dark">{os.count.toLocaleString()}</span>
        </div>
      </button>

      {isExpanded && (
        <div className="bg-white">
          {os.details.map((details, idx) => (
            <DetailsItem 
              key={idx} 
              details={details} 
              totalCount={os.count} 
              renderItem={renderItem}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Composant Device
interface DeviceItemProps {
  device: IDeviceBreakdown;
  totalSessions: number;
  isExpanded: boolean;
  expandedOS: Set<string>;
  onToggleDevice: () => void;
  onToggleOS: (osKey: string) => void;
  renderItem: RenderItem;
}

const DeviceItem: React.FC<DeviceItemProps> = ({ 
  device, 
  totalSessions, 
  isExpanded, 
  expandedOS,
  onToggleDevice, 
  onToggleOS,
  renderItem
}) => {
  const percentage = ((device.count / totalSessions) * 100).toFixed(1);

  const getDeviceIcon = (deviceName: string) => {
    const name = deviceName.toLowerCase();
    if (name.includes('mobile') || name.includes('phone')) {
      return <i className="bi bi-phone fs-5 text-white"></i>;
    } else if (name.includes('tablet')) {
      return <i className="bi bi-tablet fs-5 text-white"></i>;
    } else {
      return <i className="bi bi-display fs-5 text-white"></i>;
    }
  };

  return (
    <div className="border rounded overflow-hidden">
      <button
        onClick={onToggleDevice}
        className="btn w-100 d-flex justify-content-between align-items-center p-3 border-0 text-start bg-primary"
      >
        <div className="d-flex align-items-center gap-3 text-white">
          <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} text-white`}></i>
          {getDeviceIcon(device.device)}
          <span className="fw-semibold fs-5 text-capitalize">{device.device}</span>
        </div>
        <div className="d-flex align-items-center gap-3">
          <small className="text-white">{percentage}%</small>
          <span className="fw-bold text-white">{device.count.toLocaleString()}</span>
        </div>
      </button>

      {isExpanded && (
        <div className="bg-white">
          {device.os.map((os) => {
            const osKey = `${device.device}-${os.name}`;
            return (
              <OSItem
                key={osKey}
                os={os}
                deviceCount={device.count}
                isExpanded={expandedOS.has(osKey)}
                onToggle={() => onToggleOS(osKey)}
                renderItem={renderItem}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const DeviceStats: React.FC<DeviceStatsProps> = ({ data, title, renderItem }) => {
  const [expandedDevices, setExpandedDevices] = useState<Set<string>>(new Set());
  const [expandedOS, setExpandedOS] = useState<Set<string>>(new Set());

  const toggleDevice = (device: string) => {
    setExpandedDevices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(device)) {
        newSet.delete(device);
      } else {
        newSet.add(device);
      }
      return newSet;
    });
  };

  const toggleOS = (key: string) => {
    setExpandedOS(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const totalSessions = data.reduce((sum, device) => sum + device.count, 0);

  return (
    <div className="card mb-10">
      <div className="card-header">
        <div className="card-title">{title}</div>
      </div>
      <div className="card-body p-4">
        <div className="d-flex flex-column gap-2">
          {data.map((device) => (
            <DeviceItem
              key={device.device}
              device={device}
              totalSessions={totalSessions}
              isExpanded={expandedDevices.has(device.device)}
              expandedOS={expandedOS}
              onToggleDevice={() => toggleDevice(device.device)}
              onToggleOS={toggleOS}
              renderItem={renderItem}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DeviceStats;
